import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  hide,
  offset,
  Placement,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { cloneElement, forwardRef, isValidElement, useState } from 'react';
import React from 'react';

import { Menu, MenuProps } from '@/components/Menu';
import { Button, type ButtonProps } from '@/components/ui/button';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

export interface DropdownMenu extends MenuProps {
  placement?: Placement;
  onClose?: () => void;
  width?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * If true, the menu will match the width of the trigger button.
   */
  fullWidthMenu?: boolean;
  /**
   * Called when "Select All" is clicked in multi-select mode.
   */
  onSelectAll?: () => void;
  /**
   * Called when "Deselect All" is clicked in multi-select mode.
   */
  onDeselectAll?: () => void;
}

export interface DropdownProps extends Omit<ButtonProps, 'onSelect'> {
  menu?: DropdownMenu;
  focusType?: 'click' | 'focus';
  asChild?: boolean;
  /**
   * If true, the menu will match the width of the trigger button.
   */
  fullWidthMenu?: boolean;
  /**
   * If true, disables portal rendering and renders the menu inline.
   * Useful for edge cases like modals or constrained containers.
   */
  disablePortal?: boolean;

  // Button trigger props
  label?: React.ReactNode;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  isLoading?: boolean;
  styleType?: string;

  // ========================================
  // Legacy Compatibility Props (Deprecated)
  // ========================================
  // These props provide backward compatibility with chariot-ui Dropdown
  // and will be removed in a future version. Please migrate to the new API.

  /** @deprecated Use menu.items instead. Pass array of items with onClick callbacks. */
  options?: DropdownOption[];

  /** @deprecated Use label instead */
  placeholder?: string;

  /** @deprecated Use startIcon instead */
  icon?: React.ReactNode;

  /** @deprecated Use menu.onClick instead. Called when an option is selected. */
  onSelect?: (option: DropdownOption) => void;

  /** @deprecated Border is always visible, use custom styling instead */
  border?: boolean;

  /** @deprecated Use menu.className instead */
  listClassName?: string;

  /** @deprecated Use fullWidthMenu instead */
  fitWidth?: boolean;

  /** @deprecated Use label instead. Tooltip/title for the dropdown button. */
  title?: string;
}

export const Dropdown = forwardRef<HTMLButtonElement, DropdownProps>(
  function Dropdown(props: DropdownProps, ref) {
    const {
      menu: menuProp,
      asChild,
      focusType,
      fullWidthMenu: propFullWidthMenu,
      disablePortal = false,
      size: buttonSize,
      label,
      startIcon: propStartIcon,
      endIcon: propEndIcon,
      isLoading,
      styleType,
      // Legacy props
      options,
      placeholder,
      icon,
      onSelect,
      border, // eslint-disable-line @typescript-eslint/no-unused-vars
      listClassName,
      fitWidth,
      title,
      className: propClassName,
      ...buttonProps
    } = props;

    // Transform legacy props to new API
    const menu: DropdownMenu = React.useMemo(() => {
      // If using new API, use it directly
      if (menuProp) {
        return {
          ...menuProp,
          className: listClassName || menuProp.className,
        } as DropdownMenu;
      }

      // If using legacy API, transform to new API
      if (options) {
        return {
          items: options.map(opt => {
            // Handle dividers
            if (opt.isDivider) {
              return {
                label: '',
                value: 'divider',
                isDivider: true,
              };
            }

            return {
              label: opt.label,
              value: opt.value ?? opt.label,
              icon: opt.icon,
              disabled: opt.disabled,
              count: opt.count,
              onClick: () => {
                // Call the option's onClick if provided
                opt.onClick?.();
                // Call onSelect with the option if provided
                onSelect?.(opt);
              },
            };
          }),
          className: listClassName,
          placement: 'bottom-start' as Placement,
        } as DropdownMenu;
      }

      // Fallback - empty menu
      return { items: [] } as DropdownMenu;
    }, [menuProp, options, onSelect, listClassName]);

    const {
      placement = 'bottom-start',
      onClose,
      fullWidthMenu: menuFullWidthMenu,
      ...menuProps
    } = menu;

    // Standard controlled/uncontrolled pattern - replaces useStorage to fix FloatingUI circular dependency
    const [internalOpen, setInternalOpen] = useState(false);
    const open = menu.open !== undefined ? menu.open : internalOpen;
    const setOpen = (value: boolean) => {
      setInternalOpen(value);
      menu.onOpenChange?.(value);
    };

    // Track the width of the trigger button
    const triggerButtonRef = React.useRef<HTMLElement | null>(null);
    const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>(
      undefined
    );

    // Determine if fullWidthMenu is enabled (from either prop or menu)
    // Legacy fitWidth prop maps to fullWidthMenu
    const fullWidthMenu = propFullWidthMenu ?? menuFullWidthMenu ?? fitWidth;

    // Use label priority: label > placeholder > title
    const buttonLabel = label || placeholder || title;

    // Determine if single-select mode and get selected item with severity
    const selectedItem = React.useMemo(() => {
      if (menuProps.multiSelect) return null;

      const selectedValue = Array.isArray(menuProps.value)
        ? menuProps.value[0]
        : menuProps.value;
      if (!selectedValue) return null;

      return menuProps.items?.find(item => item.value === selectedValue);
    }, [menuProps.multiSelect, menuProps.value, menuProps.items]);

    // Use icon priority: startIcon from buttonProps > legacy icon prop > severity indicator
    const buttonStartIcon =
      propStartIcon ||
      icon ||
      (selectedItem?.severity && (
        <div
          className={`size-2.5 rounded-full border bg-chariot-status-${selectedItem.severity} border-chariot-stroke-${selectedItem.severity}`}
        />
      ));

    // When menu opens and fullWidthMenu is true, measure the trigger width
    React.useEffect(() => {
      if (open && fullWidthMenu && triggerButtonRef.current) {
        setTriggerWidth(triggerButtonRef.current.offsetWidth);
      }
    }, [open, fullWidthMenu]);

    const floatingProps = useFloating({
      placement,
      open,
      onOpenChange: (open: boolean) => {
        if (!open) {
          onClose?.();
        }

        setOpen(open);
      },
      whileElementsMounted(referenceEl, floatingEl, update) {
        const cleanup = autoUpdate(referenceEl, floatingEl, update, {
          animationFrame: true,
        });
        return cleanup;
      },
      middleware: [
        hide({
          strategy: 'referenceHidden',
        }),
        offset(10),
        flip({
          crossAxis: false,
          mainAxis: true,
          padding: 10,
        }),
        shift({
          crossAxis: false,
          mainAxis: true,
          padding: 10,
        }),
        size({
          padding: 10,
          apply({ availableHeight, elements }) {
            elements.floating.style.maxHeight = `${availableHeight}px`;
          },
        }),
      ],
    });

    const focus = useFocus(floatingProps.context);
    const click = useClick(floatingProps.context);
    const dismiss = useDismiss(floatingProps.context);
    const role = useRole(floatingProps.context);

    const interactions = useInteractions([
      focusType === 'focus' ? focus : click,
      dismiss,
      role,
    ]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const childrenRef = (props?.children as any)?.ref;
    // Compose refs: floating, forwarded, children, and our triggerButtonRef
    const triggerRef = useMergeRefs([
      floatingProps.refs.setReference,
      ref,
      childrenRef,
      triggerButtonRef,
    ]);

    // Map legacy styleType to shadcn variant
    function mapStyleTypeToVariant(styleType?: string): ButtonProps['variant'] {
      switch (styleType) {
        case 'primary':
          return 'default';
        case 'secondary':
          return 'secondary';
        case 'text':
          return 'ghost';
        case 'textPrimary':
          return 'link';
        case 'header':
          return 'secondary';
        case 'error':
          return 'destructive';
        case 'rounded':
          return 'outline';
        case 'icon':
          return 'ghost';
        case 'none':
          return 'ghost';
        case 'tertiary':
          return 'ghost';
        default:
          return 'ghost'; // Dropdown triggers are typically ghost-styled
      }
    }

    return (
      <>
        {asChild && isValidElement(props.children) ? (
          cloneElement(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FloatingUI cloneElement requires generic ReactElement type
            props.children as React.ReactElement<any>,
            // eslint-disable-next-line react-hooks/refs -- FloatingUI requires passing callback refs through getReferenceProps during render for anchor registration
            interactions.getReferenceProps({
              ref: triggerRef,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...(props.children as unknown as any).props,
            })
          )
        ) : (
          <Button
            ref={triggerRef}
            size={buttonSize}
            variant={mapStyleTypeToVariant(styleType)}
            className={cn(
              styleType === 'rounded' && 'rounded-full',
              propClassName
            )}
            disabled={isLoading || undefined}
            {...interactions.getReferenceProps(buttonProps)}
          >
            {buttonStartIcon}
            {buttonLabel || props.children}
            {propEndIcon}
          </Button>
        )}
        {open &&
          (disablePortal ? (
            <FloatingFocusManager
              context={floatingProps.context}
              modal={false}
              initialFocus={menuProps.search ? 0 : -1}
            >
              <div
                data-testid="dropdown-menu"
                ref={floatingProps.refs.setFloating}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 10px)',
                  left: placement.includes('end') ? 'auto' : 0,
                  right: placement.includes('end') ? 0 : 'auto',
                  width: fullWidthMenu ? triggerWidth : menuProps.width,
                  zIndex: 50,
                }}
                className="scrollbar-dark flex outline-hidden"
                {...interactions.getFloatingProps()}
              >
                <Menu
                  {...menuProps}
                  onClick={updatedValue => {
                    menuProps?.onClick?.(updatedValue);

                    if (!menuProps.multiSelect) {
                      setOpen(false);
                    }
                  }}
                />
              </div>
            </FloatingFocusManager>
          ) : (
            <FloatingPortal>
              {/* eslint-disable react-hooks/refs -- FloatingUI requires context, callback refs, floatingStyles, and middlewareData to be accessed during render as part of its positioning protocol */}
              <FloatingFocusManager
                context={floatingProps.context}
                modal={false}
                initialFocus={menuProps.search ? 0 : -1}
              >
                <div
                  data-testid="dropdown-menu"
                  ref={floatingProps.refs.setFloating}
                  style={{
                    ...floatingProps.floatingStyles,
                    width: fullWidthMenu ? triggerWidth : menuProps.width,
                    visibility: floatingProps.middlewareData.hide
                      ?.referenceHidden
                      ? 'hidden'
                      : 'visible',
                    pointerEvents: 'auto',
                  }}
                  className="scrollbar-dark z-[9999] flex outline-hidden"
                  {...interactions.getFloatingProps()}
                >
                  <Menu
                    {...menuProps}
                    onClick={updatedValue => {
                      menuProps?.onClick?.(updatedValue);

                      if (!menuProps.multiSelect) {
                        setOpen(false);
                      }
                    }}
                  />
                </div>
              </FloatingFocusManager>
              {/* eslint-enable react-hooks/refs */}
            </FloatingPortal>
          ))}
      </>
    );
  }
);
